import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import SharePointUpload from '../components/SharePointUpload';

const STAGES = ['initiation','drafting','review','negotiation','execution','active','renewal_notice','renewal_expiry'];
const STAGE_LABELS = { initiation:'1. Initiation', drafting:'2. Drafting', review:'3. Review', negotiation:'4. Negotiation', execution:'5. Execution', active:'6. Active', renewal_notice:'7. Renewal Notice', renewal_expiry:'8. Renewal / Expiry' };

export default function AgreementLifecycle({ showToast, editMode }) {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'', type:'', counterparty:'', start_date:'', expiry_date:'', value_ngn:'', notes:'' });
  const [importFile, setImportFile] = useState(null);

  useEffect(() => { loadAgreements(); }, []);

  async function loadAgreements() {
    try { const { data } = await api.getAgreements(); setAgreements(data || []); }
    catch (err) { showToast('Failed to load agreements'); }
    finally { setLoading(false); }
  }

  async function submitAgreement() {
    try {
      await api.createAgreement({ ...form, value_ngn: parseFloat(form.value_ngn) || null });
      showToast('Agreement registered'); setShowForm(false); loadAgreements();
    } catch (err) { showToast('Registration failed'); }
  }

  async function handleImport() {
    if (!importFile) { showToast('Select a CSV or Excel file'); return; }
    try {
      const { data } = await api.importAgreements(importFile);
      showToast(data.message); loadAgreements();
    } catch (err) { showToast('Import failed'); }
  }

  function daysUntilExpiry(date) {
    if (!date) return null;
    return Math.ceil((new Date(date) - new Date()) / (1000*60*60*24));
  }

  const expiringSoon = agreements.filter(a => { const d = daysUntilExpiry(a.expiry_date); return d !== null && d > 0 && d <= 30; });

  return (
    <div>
      <div className="section-header">
        <div className="section-title">📅 Agreement Lifecycle Manager</div>
        <div style={{display:'flex', gap:8}}>
          <a className="topbar-btn btn-ghost" href={`${process.env.REACT_APP_API_URL || 'https://del-legal-api.azurewebsites.net/api'}/agreements/import/template`} download>📄 Download Template</a>
          <button className="topbar-btn btn-ghost" onClick={() => document.getElementById('csv-import').click()}>📥 Import CSV / Excel</button>
          <input id="csv-import" type="file" accept=".csv,.xlsx,.xls" style={{display:'none'}} onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { const { data } = await api.importAgreements(f); showToast(data.message); loadAgreements(); } catch(err) { showToast('Import failed: ' + (err.response?.data?.error || err.message)); } e.target.value=''; }} />
          <button className="topbar-btn btn-primary" onClick={() => setShowForm(!showForm)}>+ Register Agreement</button>
        </div>
      </div>

      {/* Alerts */}
      {expiringSoon.map(a => {
        const d = daysUntilExpiry(a.expiry_date);
        return <div key={a.id} className={`alert ${d<=14?'alert-red':'alert-gold'}`}>{d<=14?'🔴':'⚠️'} {a.title} expires in {d} days. {d<=14?'Immediate renewal action required.':'Renewal recommended.'}</div>;
      })}

      {/* Lifecycle Stage Tracker */}
      <div className="card" style={{marginBottom:18}}>
        <div className="card-title">📊 Agreement Lifecycle Stages</div>
        <div className="lifecycle-stages">
          {STAGES.map(s => (
            <div key={s} className={`stage ${s==='active'?'current':STAGES.indexOf(s)<STAGES.indexOf('active')?'done':''}`}>
              {STAGE_LABELS[s]}
            </div>
          ))}
        </div>
        <div style={{fontSize:12, color:'var(--muted)', marginTop:8}}>Stage tracker shown for reference. Each agreement tracks its own lifecycle stage independently.</div>
      </div>

      {/* New Agreement Form */}
      {showForm && (
        <div className="card" style={{marginBottom:18}}>
          <div className="card-title">📝 Register New Agreement</div>
          <div className="form-grid">
            <div className="form-group full"><label>Agreement Title</label><input type="text" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Lagos HQ Office Tenancy" /></div>
            <div className="form-group"><label>Type</label><select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}><option value="">-- Select --</option><option>Tenancy/Lease</option><option>EPC/Subcontract</option><option>SLA</option><option>Employment</option><option>MOU</option><option>NDA</option><option>JV Agreement</option><option>Insurance</option><option>Fleet Lease</option><option>LOI</option></select></div>
            <div className="form-group"><label>Counterparty</label><input type="text" value={form.counterparty} onChange={e=>setForm({...form,counterparty:e.target.value})} placeholder="Company name" /></div>
            <div className="form-group"><label>Start Date</label><input type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} /></div>
            <div className="form-group"><label>Expiry Date</label><input type="date" value={form.expiry_date} onChange={e=>setForm({...form,expiry_date:e.target.value})} /></div>
            <div className="form-group"><label>Value (NGN)</label><input type="number" value={form.value_ngn} onChange={e=>setForm({...form,value_ngn:e.target.value})} placeholder="e.g. 24000000" /></div>
            <div className="form-group"><label>Notes</label><input type="text" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Additional details" /></div>
          </div>
          <div className="form-actions">
            <button className="topbar-btn btn-ghost" onClick={()=>setShowForm(false)}>Cancel</button>
            <button className="topbar-btn btn-primary" onClick={submitAgreement}>Register</button>
          </div>
        </div>
      )}

      {/* Agreement Register */}
      <div className="card">
        <div className="card-title">📁 Agreement Register</div>
        {loading ? <div className="loading">Loading...</div> : (
          editMode ? (
            <EditableDataTable
              columns={[{key:'ref',label:'Ref'},{key:'title',label:'Title'},{key:'type',label:'Type'},{key:'counterparty',label:'Counterparty'},{key:'start_date',label:'Start'},{key:'expiry_date',label:'Expiry'},{key:'value_ngn',label:'Value'},{key:'status',label:'Status'}]}
              data={agreements}
              editable={true}
              onDataChange={(updated) => setAgreements(updated)}
              renderCell={(row, colKey) => {
                if (colKey === 'ref') return <span style={{fontWeight:600,color:'var(--mid)',fontSize:10}}>{row.ref}</span>;
                if (colKey === 'type') return <span className="badge badge-blue">{row.type}</span>;
                if (colKey === 'value_ngn') return row.value_ngn ? '₦'+Number(row.value_ngn).toLocaleString() : '-';
                if (colKey === 'start_date' || colKey === 'expiry_date') return row[colKey] ? new Date(row[colKey]).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-';
                if (colKey === 'status') return <span className={`badge ${row.status==='active'?'badge-green':row.status==='expired'?'badge-red':'badge-gold'}`}>{row.status||'draft'}</span>;
                return row[colKey] ?? '-';
              }}
            />
          ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Ref</th><th>Agreement Title</th><th>Type</th><th>Counterparty</th><th>Start</th><th>Expiry</th><th>Value</th><th>Days Left</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {agreements.map(a => {
                  const days = daysUntilExpiry(a.expiry_date);
                  const daysColor = days===null?'var(--muted)':days<=30?'var(--danger)':days<=60?'var(--warn)':'var(--success)';
                  const statusBdg = days!==null && days<=0 ? 'badge-red' : days!==null && days<=30 ? 'badge-red' : days!==null && days<=90 ? 'badge-gold' : 'badge-green';
                  const statusLabel = days!==null && days<=0 ? 'Expired' : days!==null && days<=30 ? 'Expiring' : days!==null && days<=90 ? 'Renewal Due' : 'Active';
                  return (
                    <tr key={a.id}>
                      <td style={{fontWeight:600, color:'var(--mid)', fontSize:10}}>{a.ref}</td>
                      <td>{a.title}</td>
                      <td><span className="badge badge-blue">{a.type}</span></td>
                      <td>{a.counterparty || '-'}</td>
                      <td>{a.start_date ? new Date(a.start_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-'}</td>
                      <td>{a.expiry_date ? new Date(a.expiry_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-'}</td>
                      <td style={{fontWeight:600}}>{a.value_ngn ? '₦'+Number(a.value_ngn).toLocaleString() : '-'}</td>
                      <td style={{fontWeight:700, color:daysColor}}>{days !== null ? `${days} days` : '-'}</td>
                      <td><span className={`badge ${statusBdg}`}>{statusLabel}</span></td>
                      <td>{days!==null && days<=90 ? <button className="sig-btn primary">Renew</button> : <button className="sig-btn ghost">View</button>}</td>
                    </tr>
                  );
                })}
                {agreements.length===0 && <tr><td colSpan={10} style={{textAlign:'center',color:'var(--muted)'}}>No agreements registered</td></tr>}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>
      <SharePointUpload linkedEntityType="agreement" showToast={showToast} />
    </div>
  );
}
